// Kitchen on a wall tablet (docs/plans/stations.md, boards KitchenWall, KitchenOffline,
// KitchenEmpty). The same tickets as the Orders page's Kitchen tab, oldest first.
import { formatQty, kotLines, ticketStanding } from '@ims/core'
import { Badge, Button, Card, EmptyState, arrivalClass, cx, useArrivals } from '@ims/ui'
import { AlertTriangle, Check, ChefHat } from '@ims/ui/icons'
import { useEffect, useEffectEvent, useRef } from 'react'
import { useSearchParams } from 'react-router'
import { useNow, useOnline } from '../hooks.ts'
import { newIds, soundOn } from '../lib/alerts.ts'
import {
  STATION_LABEL,
  ticketName,
  clock,
  closedLabel,
  closedTone,
  orderItemRows,
  orderName,
  stillToMake,
  ticketLevel,
  ticketSoundKeys,
} from '../lib/orders.ts'
import { useSession } from '../session.tsx'
import { useCan } from '../workspace.tsx'
import { useKitchenTickets } from './floor.ts'
import { TicketMore, useTicketActions } from './restaurant.tsx'
import { useOfflineSince } from './station-mode.ts'

const LEVEL = {
  ok: { top: 'border-t-border', pill: 'bg-soft text-muted' },
  warn: { top: 'border-t-warning', pill: 'bg-warning-light text-warning' },
  late: { top: 'border-t-danger', pill: 'bg-danger-light text-danger' },
} as const

export function StationKitchenScreen() {
  const [params] = useSearchParams()
  const station = params.get('station')
  const all = useKitchenTickets()
  const canServe = useCan('waiter')
  const now = useNow()
  const { platform } = useSession()
  const { move, clear, print } = useTicketActions()
  const online = useOnline()
  const since = useOfflineSince()

  const seen = useRef<Set<string> | null>(null)
  const chime = useEffectEvent(() => {
    if (soundOn()) platform.alerts.chime()
  })
  useEffect(() => {
    if (!all) return
    const ids = ticketSoundKeys(all, Date.now())
    if (newIds(seen.current, ids).length > 0) chime()
    seen.current = new Set(ids)
  }, [all])

  const tickets = (all ?? []).filter((t) => !station || t.ticket.station === station)
  const arrived = useArrivals(
    tickets.map((t) => t.ticket.id),
    all !== undefined,
    station ?? '',
  )
  const nowMs = now.getTime()
  const toMake = stillToMake(
    tickets
      .filter((t) => ticketStanding(t.order, nowMs) !== 'stopped')
      .flatMap((t) => kotLines(orderItemRows(t.rows))),
  )
  const warning = !online && (
    <div
      role="alert"
      className="flex items-center gap-2.5 bg-danger-light px-4 py-2.5 font-semibold text-danger"
    >
      <AlertTriangle className="size-[18px] shrink-0" aria-hidden />
      <span>
        No internet{since ? ` since ${clock(since)}` : ''}. New orders can’t reach this screen. Ask
        the counter for printed tickets.
      </span>
    </div>
  )
  if (all && tickets.length === 0)
    return (
      <>
        {warning}
        <div className="p-6">
          <Card className="p-0">
            <EmptyState
              icon={<ChefHat className="size-6" />}
              title="Nothing waiting"
              description="A ticket shows here when an order is sent to the kitchen."
            />
          </Card>
        </div>
      </>
    )
  return (
    <>
      {warning}
      {toMake.length > 0 && (
        <div
          aria-label="Still to make"
          className="flex flex-wrap items-baseline gap-x-5 gap-y-1 border-b border-border bg-kitchen-wash px-4 py-2.5"
        >
          <span className="text-sm font-semibold text-kitchen">Still to make</span>
          {toMake.map((l) => (
            <span key={l.description} className="whitespace-nowrap">
              <b className="tabular text-lg font-extrabold">{formatQty(l.qtyMilli)}</b>{' '}
              <span className="text-muted">{l.description}</span>
            </span>
          ))}
        </div>
      )}
      <ul className="grid items-start gap-3 p-3.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {tickets.map((t) => {
          const minutes = Math.max(
            0,
            Math.floor((now.getTime() - new Date(t.ticket.sent_at).getTime()) / 60_000),
          )
          const level = LEVEL[ticketLevel(minutes)]
          const standing = ticketStanding(t.order, nowMs)
          // A paid ticket's cook just finishes or clears it: three buttons don't fit a narrow card.
          const startable = t.ticket.status === 'new' && standing !== 'paid'
          if (standing === 'stopped')
            return (
              <li key={t.ticket.id} className="rounded-card">
                <Card className="flex flex-col border-t-4 border-danger! bg-danger-light! p-0">
                  <div className="border-b border-danger/30 px-3.5 py-3">
                    <h2 className="truncate text-xl font-extrabold">
                      {ticketName(t.ticket)} ·{' '}
                      {t.order ? orderName(t.order, t.tableName) : (t.tableName ?? 'No table')}
                    </h2>
                    <p className="flex items-center gap-1.5 font-bold text-danger">
                      <AlertTriangle className="size-[18px]" aria-hidden />
                      Cancelled, stop
                    </p>
                  </div>
                  <ul className="space-y-1.5 px-3.5 py-3 text-muted line-through">
                    {kotLines(orderItemRows(t.rows)).map((l, i) => (
                      <li key={i} className="text-lg font-semibold">
                        <span className="tabular inline-block min-w-9 font-extrabold">
                          {formatQty(l.qtyMilli)}×
                        </span>
                        {l.description}
                      </li>
                    ))}
                  </ul>
                  {t.order?.void_reason && (
                    <p className="px-3.5 pb-3 text-sm text-danger">{t.order.void_reason}</p>
                  )}
                  <div className="border-t border-danger/30 p-2.5">
                    <Button
                      size="lg"
                      variant="secondary"
                      className="h-14 w-full"
                      disabled={!canServe}
                      aria-label={`Got it, ticket ${t.ticket.kot_no}`}
                      onClick={() => void clear(t)}
                    >
                      Got it
                    </Button>
                  </div>
                </Card>
              </li>
            )
          return (
            <li
              key={t.ticket.id}
              className={cx('rounded-card', arrived.has(t.ticket.id) && arrivalClass)}
            >
              <Card className={cx('flex flex-col border-t-4 p-0', level.top)}>
                <div className="flex items-start justify-between gap-2 border-b border-border px-3.5 py-3">
                  <div className="min-w-0">
                    <h2 className="truncate text-xl font-extrabold">
                      {ticketName(t.ticket)} ·{' '}
                      {t.order ? orderName(t.order, t.tableName) : (t.tableName ?? 'No table')}
                    </h2>
                    <p
                      className={cx(
                        'flex flex-wrap items-center gap-x-1.5 text-sm font-semibold',
                        t.ticket.status === 'preparing' ? 'text-warning' : 'text-muted',
                      )}
                    >
                      {station ? '' : `${STATION_LABEL[t.ticket.station]} · `}
                      {t.ticket.status === 'preparing' ? 'Preparing' : 'New'}
                      {standing === 'paid' && (
                        <Badge tone={closedTone(t.order)}>{closedLabel(t.order)}</Badge>
                      )}
                    </p>
                  </div>
                  <span
                    className={cx(
                      'tabular shrink-0 rounded-lg px-2.5 py-1 text-xl font-extrabold',
                      level.pill,
                    )}
                  >
                    {minutes} min
                  </span>
                </div>
                <ul className="space-y-1.5 px-3.5 py-3">
                  {kotLines(orderItemRows(t.rows)).map((l, i) => (
                    <li
                      key={i}
                      className={cx('text-lg font-semibold', l.cancelled && 'text-danger')}
                    >
                      <span className="tabular inline-block min-w-9 font-extrabold">
                        {formatQty(l.qtyMilli)}×
                      </span>
                      {l.cancelled && 'Cancel '}
                      {l.description}
                      {l.note && (
                        <span className="block pl-9 text-base font-semibold text-warning">
                          {l.note}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
                <div className="flex gap-2 border-t border-border p-2.5">
                  {startable && (
                    <Button
                      size="lg"
                      className="h-14 flex-1"
                      disabled={!canServe}
                      aria-label={`Start ticket ${t.ticket.kot_no}`}
                      onClick={() => void move(t, 'preparing')}
                    >
                      Start
                    </Button>
                  )}
                  <Button
                    size="lg"
                    className="h-14 flex-1"
                    variant={startable ? 'secondary' : 'primary'}
                    icon={startable ? undefined : <Check className="size-5" />}
                    disabled={!canServe}
                    aria-label={`Ticket ${t.ticket.kot_no} ready`}
                    onClick={() => void move(t, 'ready')}
                  >
                    Ready
                  </Button>
                  {standing === 'paid' && (
                    <Button
                      size="lg"
                      variant="secondary"
                      className="h-14 flex-1"
                      disabled={!canServe}
                      aria-label={`Clear ticket ${t.ticket.kot_no}`}
                      onClick={() => void clear(t)}
                    >
                      Clear
                    </Button>
                  )}
                  <TicketMore ticket={t.ticket} onPrint={() => print(t)} large />
                </div>
              </Card>
            </li>
          )
        })}
      </ul>
    </>
  )
}
